import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { Role } from "../roles.js";
import { ExplanationsToggle, ThemeToggle } from "../components.js";
import { Button } from "../components/controls.js";
import { useSession } from "../auth/session.js";
import symbolDark from "../assets/senesight-symbol-ondark.svg";
import symbolLight from "../assets/senesight-symbol-onlight.svg";

/**
 * The top bar (.bar) from the locked demo: logo + primary nav + theme toggle. Nav is role-gated —
 * the shell only shows what the request's role is permitted to see (E8.2-AC2). Both logo glyphs
 * render; the theme CSS shows the one for the active theme.
 *
 * AB#948 — mobile: the locked design has no mobile nav pattern, so the horizontal `.menu` collapses
 * to a drawer below the phone/small-tablet breakpoint (styled in responsive.css from the demo's own
 * popover tokens). The `.navtoggle` hamburger is CSS-hidden at every desktop width, so the desktop
 * DOM renders identically to the locked design. The drawer's open/closed state is JS (not matchMedia),
 * so it works the same regardless of viewport; CSS decides whether the nav shows inline or as a drawer.
 */

export interface NavItem {
  readonly key: string;
  readonly label: string;
  readonly path: string;
  readonly roles: readonly Role[];
}

const ALL_ROLES: readonly Role[] = [
  "admin",
  "delivery-lead",
  "sponsor",
  "client-viewer",
  "readonly",
];
// NX-2645: Forecast / Capability / Reliability / Friction were nav destinations that the locked
// design does not have. design/08 §1 places reliability, capability-vs-target and DevEx friction as
// CHILDREN of Project detail, and its §3 screen inventory lists exactly seven screens — Portfolio,
// Program rollup, Project detail, Evidence drawer, Assessment, Connectors, Provenance popover. Only
// Connectors is cross-cutting. Those four entries came in with E8.2's "locked demo chrome" but are
// absent from both prototypes, so they were four permanent ComingSoon stubs promising screens nobody
// had designed. Their content is not cancelled — it lands as project-detail panels (E8.7, NX-2632).
export const NAV: readonly NavItem[] = [
  { key: "portfolio", label: "Portfolio", path: "/portfolio", roles: ALL_ROLES },
  {
    key: "review-queue",
    label: "Review queue",
    path: "/review-queue",
    roles: ["admin", "delivery-lead"],
  },
  { key: "connectors", label: "Connectors", path: "/connectors", roles: ["admin"] },
  // E8.10 (NX-2638) — visible to every role, matching CAPABILITY_ASSESSMENT_VIEW_ROLES. Submission
  // is narrower and is gated on the screen and, decisively, by the API.
  { key: "assessment", label: "Assessment", path: "/assessment", roles: ALL_ROLES },
  // E8.14 (NX-2643) — Timeline. NOT a reversal of the NX-2645 comment above: NX-2645 retired four
  // nav entries because the locked IA (design/08 §1) had no such screens and placed their content
  // as Project-detail panels instead. Timeline is a DELIBERATE exception, ruled by Papa on
  // 2026-08-21: the implementation plan (E8.14, M2) wins here, the locked IA simply predates that
  // call, and the design doc is being updated separately (not by this change). If you are reading
  // this because a future NX-2645-style cleanup looks tempting: check the design doc's current
  // state first, this was not an oversight.
  { key: "timeline", label: "Timeline", path: "/timeline", roles: ALL_ROLES },
  // NX-2745 (E8.15) — the plan's org-wide calendar destination, same ruling family as Timeline.
  { key: "calendar", label: "Calendar", path: "/calendar", roles: ALL_ROLES },
  // NX-2737 (E12.3) — internal ops console: nav renders for admins only; the API is the control.
  { key: "ops", label: "Ops", path: "/ops", roles: ["admin"] },
  // NX-2855 — admin portfolio registry management (declared create/edit/profile/reparent).
  { key: "admin-portfolio", label: "Admin portfolio", path: "/admin/portfolio", roles: ["admin"] },
];

/** The nav a role is permitted to see (E8.2-AC2). */
export function navForRole(role: Role): NavItem[] {
  return NAV.filter((item) => item.roles.includes(role));
}

export function TopBar(): React.ReactNode {
  const { session, signOut } = useSession();
  const items = session ? navForRole(session.role) : [];
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close the mobile drawer whenever the route changes (link tap, breadcrumb, browser back), so it
  // never lingers open over the newly navigated page.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <div className="bar">
      <NavLink className="logo" to="/portfolio">
        <img className="sym ondark" src={symbolDark} alt="" width={28} height={28} />
        <img className="sym onlight" src={symbolLight} alt="" width={28} height={28} />
        <div>
          SeneSight<small>Delivery Intelligence</small>
        </div>
      </NavLink>
      <nav id="primary-nav" className={menuOpen ? "menu open" : "menu"} aria-label="Primary">
        {items.map((item) => (
          <NavLink
            key={item.key}
            to={item.path}
            className={({ isActive }) => (isActive ? "on" : "")}
            onClick={() => setMenuOpen(false)}
          >
            {item.label}
          </NavLink>
        ))}
        {session && (
          <Button
            size="sm"
            variant="ghost"
            className="signout-mobile"
            onClick={() => {
              setMenuOpen(false);
              signOut();
            }}
          >
            Sign out
          </Button>
        )}
      </nav>
      <div className="right">
        {items.length > 0 && (
          <button
            type="button"
            className="navtoggle"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-controls="primary-nav"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="glyph" aria-hidden="true" />
          </button>
        )}
        <ExplanationsToggle />
        <ThemeToggle />
        {session && (
          <Button size="sm" variant="ghost" className="signout-desktop" onClick={signOut}>
            Sign out
          </Button>
        )}
      </div>
    </div>
  );
}
